import React, { useEffect, useMemo, useState } from "react";
import { Box, Grid, Stack, Text, Title } from "@mantine/core";
import { FormSchema, FormTemplateField } from "../../../types/formTemplates";
import { FieldRenderer } from "../FieldRenderer";
import { PaginatedShell } from "../PaginatedShell";

interface TemplatePreviewProps {
  name: string;
  description?: string | null;
  schema: FormSchema;
}

function splitIntoPages(fields: FormTemplateField[]): FormTemplateField[][] {
  const pages: FormTemplateField[][] = [[]];
  for (const f of fields) {
    if (f.type === "page_break") {
      pages.push([]);
    } else {
      pages[pages.length - 1].push(f);
    }
  }
  return pages.length === 0 ? [[]] : pages;
}

export const TemplatePreview: React.FC<TemplatePreviewProps> = ({
  name,
  description,
  schema,
}) => {
  const [pageIndex, setPageIndex] = useState(0);

  const pages = useMemo(() => splitIntoPages(schema.fields), [schema.fields]);

  useEffect(() => {
    if (pageIndex > pages.length - 1) {
      setPageIndex(Math.max(0, pages.length - 1));
    }
  }, [pages.length, pageIndex]);

  const pageNodes = pages.map((pageFields, i) => (
    <Box key={`page-${i}`}>
      <Grid gutter="md">
        {pageFields.map((field) => {
          const span = field.layout?.colSpan ?? 12;
          return (
            <Grid.Col
              key={field.id}
              span={{ base: 12, sm: span === 6 ? 6 : 12 }}
            >
              <FieldRenderer field={field} mode="preview" />
            </Grid.Col>
          );
        })}
        {pageFields.length === 0 && (
          <Grid.Col span={12}>
            <Text c="dimmed" size="sm" ta="center">
              Empty page.
            </Text>
          </Grid.Col>
        )}
      </Grid>
    </Box>
  ));

  return (
    <PaginatedShell
      pages={pageNodes}
      activeIndex={pageIndex}
      onChange={setPageIndex}
      contentStyle={{
        padding: 24,
        background: "var(--mantine-color-white)",
        borderRadius: 12,
        border: "1px solid var(--mantine-color-gray-2)",
      }}
      header={
        <Stack gap={4} mb="md">
          <Title order={3}>{name || "Untitled Form"}</Title>
          {description && (
            <Text c="dimmed" size="sm">
              {description}
            </Text>
          )}
        </Stack>
      }
    />
  );
};
